import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByNodeId, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
// import * as React from "react"; // Poolcorp Custom Code
import { css } from "styled-components";

/* Poolcorp Custom Code */
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import { HasInvoicePaymentAuthorizationContext, withInvoicePaymentAuthorization } from "@store/Components/Common/InvoicePaymentAuthorizationContext";
import { getStoreSession } from "@store/Store/Context/ContextSelectors";
import { InvoicePaymentAuthorizedModel } from "@store/Types/InvoicePayment";
import isTheme from "@store/utilities/isTheme";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";


const mapStateToProps = (state: ApplicationState) => {
    const session = getStoreSession(state);
    return {
        currentBillTo: getCurrentBillToState(state).value,
        isAuthenticatedOrRemembered: session.isAuthenticated || session.rememberMe
    };
};
/* Poolcorp Custom Code */

const enum fields {
    links = "links",
    customCSS = "customCSS",
    // Poolcorp Custom Code
    destination = "destination",
    openInNewWindow = "openInNewWindow",
    canPayOnly = "canPayOnly",
    overrideTitle = "overrideTitle",
    hideForCanada = "hideForCanadianCustomers"
}

/* Poolcorp Custom Code */
export interface LinksModel {
    fields: {
        [fields.destination]: LinkFieldValue;
        [fields.canPayOnly]: boolean;
        [fields.openInNewWindow]: boolean;
        [fields.overrideTitle]: string;
        [fields.hideForCanada]: boolean;
    };
}
/* Poolcorp Custom Code */

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinksModel[]; // Poolcorp Custom Code
        [fields.customCSS]: string;
    };
    extendedStyles?: HeaderLinkListStyles;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasInvoicePaymentAuthorizationContext; // Poolcorp Custom Code

export interface HeaderLinkListStyles {
    headerLinkListWrapper?: InjectableCss;
    link?: LinkPresentationProps;
}

export const headerLinkListStyles: HeaderLinkListStyles = {
    headerLinkListWrapper: {},
    link: {
        css: css`
            &:visited {
                color: ${getColor("header.topContrast")};
            }
        `,
        mergeCss: true,
    },
};

const defaultCustomCss = `.list-wrapper {
}

.list-link {
}
`;

export const HeaderLinkList: FC<Props> = ({
    fields,
    extendedStyles,
    // Poolcorp Custom Code
    invoicePaymentAuthorization,
    currentBillTo
}) => {
    /* Poolcorp Custom Code */
    const isCustomerCanPay = invoicePaymentAuthorization.canPay;
    const customerCountry = currentBillTo?.country?.abbreviation;
    const isCountryCanada = customerCountry === "CA";
    /* Poolcorp Custom Code */

    const links = useGetLinks(fields.links, o => o.fields.destination);

    const styles = useMergeStyles("headerLinkList", headerLinkListStyles, extendedStyles);

    if (links.length < 1) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...styles.customCssWrapper}>
            {links.map(
                /* Poolcorp Custom Code */
                (link, index) =>
                    link.url &&
                       (( fields.links[index].fields.hideForCanadianCustomers && !isCountryCanada) || !fields.links[index].fields.hideForCanadianCustomers) &&
                            (fields.links[index].fields.canPayOnly && !isCustomerCanPay ? null : (
                        <Link
                            key={`${link.url}.${link.title}`}
                            href={link.url}
                            target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                            {...styles.link}
                        >
                            {fields.links[index].fields.overrideTitle || link.title}
                        </Link>
                    )),
                /* Poolcorp Custom Code */
            )}
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withInvoicePaymentAuthorization(HeaderLinkList)), // Poolcorp Custom Code
    definition: {
        group: "Header",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Translatable", // Poolcorp Custom Code
                fieldDefinitions: [
                    /* Poolcorp Custom Code */
                    {
                        name: fields.destination,
                        displayName: "Destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: fields.overrideTitle,
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.openInNewWindow,
                        displayName: "Open In New Window",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: fields.canPayOnly,
                        displayName: "Visible only to customers who can pay",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: fields.hideForCanada,
                        displayName: 'Hide for Canadian Customers',
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    /* Poolcorp Custom Code */
                ],
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
